import { useEffect, useMemo, useState } from 'react';
import { List, useTable } from '@refinedev/antd';
import type { CrudFilters } from '@refinedev/core';
import { Table, Tag, Space, Input, Select, DatePicker, Button, Card, Row, Col, Statistic } from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  HomeOutlined,
  DollarOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api-client';

const { RangePicker } = DatePicker;

interface HotelBooking {
  id: number;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'FAILED';
  hotelId: string;
  hotelName: string;
  city: string | null;
  country: string | null;
  checkin: string;
  checkout: string;
  adults: number;
  children: number;
  currency: string;
  totalAmount: number;
  holderFirstName: string;
  holderLastName: string;
  holderPhone: string | null;
  liteApiBookingId: string | null;
  hotelConfirmationCode: string | null;
  createdAt: string;
  user: { id: number; fullName: string | null; phone: string } | null;
}

interface HotelStats {
  total: number;
  byStatus: Record<string, number>;
  confirmedRevenue: number;
  dailyLast30d: { date: string; count: number }[];
}

const statusColors: Record<string, string> = {
  PENDING: 'orange',
  CONFIRMED: 'green',
  CANCELLED: 'default',
  FAILED: 'red',
};

const statusLabels: Record<string, string> = {
  PENDING: 'Kutilmoqda',
  CONFIRMED: 'Tasdiqlangan',
  CANCELLED: 'Bekor qilingan',
  FAILED: 'Xato',
};

const formatCurrency = (value: number, currency = 'UZS') => {
  return `${new Intl.NumberFormat('ru-RU').format(value)} ${currency}`;
};

const formatDate = (date: string) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('uz-UZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const HotelBookingList = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [stats, setStats] = useState<HotelStats | null>(null);

  useEffect(() => {
    apiClient
      .get<HotelStats>('/admin/hotels/stats')
      .then((res) => setStats(res.data))
      .catch(() => setStats(null));
  }, []);

  // Non-permanent filters only (Refine gotcha): a `permanent` filter set
  // doesn't refetch when it transitions to empty (e.g. clearing the last
  // active filter). Instead, every local filter change pushes the FULL
  // combined set via `setFilters(..., 'replace')` below.
  const sharedFilters = useMemo<CrudFilters>(() => {
    const out: CrudFilters = [];
    if (statusFilter) out.push({ field: 'status', operator: 'eq', value: statusFilter });
    if (searchFilter) out.push({ field: 'search', operator: 'eq', value: searchFilter });
    if (dateRange?.[0]) {
      out.push({ field: 'dateFrom', operator: 'gte', value: dateRange[0].format('YYYY-MM-DD') });
    }
    if (dateRange?.[1]) {
      out.push({ field: 'dateTo', operator: 'lte', value: dateRange[1].format('YYYY-MM-DD') });
    }
    return out;
  }, [statusFilter, searchFilter, dateRange]);

  const { tableProps, setFilters, setCurrent } = useTable<HotelBooking>({
    resource: 'admin/hotels/bookings',
    syncWithLocation: true,
    pagination: { pageSize: 20 },
    filters: { initial: [] },
  });

  const didMountRef = useMemo(() => ({ current: false }), []);

  useEffect(() => {
    setFilters(sharedFilters, 'replace');
    if (didMountRef.current) {
      setCurrent(1);
    } else {
      didMountRef.current = true;
    }
  }, [sharedFilters, setFilters, setCurrent, didMountRef]);

  return (
    <List title="Mehmonxona bronlari" headerButtons={() => null}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic
              title="Jami bronlar"
              value={stats?.total ?? 0}
              prefix={<HomeOutlined style={{ color: '#595959' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic
              title="Tasdiqlangan"
              value={stats?.byStatus?.CONFIRMED ?? 0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CalendarOutlined style={{ color: '#3f8600' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic
              title="Bekor qilingan"
              value={stats?.byStatus?.CANCELLED ?? 0}
              valueStyle={{ color: '#8c8c8c' }}
              prefix={<CloseCircleOutlined style={{ color: '#8c8c8c' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card>
            <Statistic
              title="Daromad (tasdiqlangan)"
              value={formatCurrency(stats?.confirmedRevenue ?? 0)}
              valueStyle={{ color: '#1890ff' }}
              prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="Holat"
          allowClear
          style={{ width: 180 }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
          options={[
            { value: 'PENDING', label: 'Kutilmoqda' },
            { value: 'CONFIRMED', label: 'Tasdiqlangan' },
            { value: 'CANCELLED', label: 'Bekor qilingan' },
            { value: 'FAILED', label: 'Xato' },
          ]}
        />
        <RangePicker
          value={dateRange}
          onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
          format="YYYY-MM-DD"
        />
        <Input.Search
          placeholder="Mijoz, mehmonxona, kod..."
          allowClear
          prefix={<SearchOutlined />}
          style={{ width: 260 }}
          onSearch={(value) => setSearchFilter(value || undefined)}
        />
      </Space>

      <Table
        {...tableProps}
        rowKey="id"
        size="small"
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
      >
        <Table.Column dataIndex="id" title="ID" width={70} />
        <Table.Column
          title="Mijoz"
          width={200}
          render={(_: unknown, record: HotelBooking) => (
            <div>
              <div style={{ fontWeight: 500 }}>
                {record.user?.fullName || `${record.holderFirstName} ${record.holderLastName}`}
              </div>
              <div style={{ color: '#7F8C8D', fontSize: 12 }}>
                {record.user?.phone || record.holderPhone || '-'}
              </div>
            </div>
          )}
        />
        <Table.Column
          title="Mehmonxona"
          render={(_: unknown, record: HotelBooking) => (
            <div>
              <div style={{ fontWeight: 500 }}>{record.hotelName}</div>
              <div style={{ color: '#7F8C8D', fontSize: 12 }}>
                {[record.city, record.country].filter(Boolean).join(', ') || '-'}
              </div>
            </div>
          )}
        />
        <Table.Column
          title="Sana"
          width={170}
          render={(_: unknown, record: HotelBooking) => (
            <span>
              {formatDate(record.checkin)} – {formatDate(record.checkout)}
            </span>
          )}
        />
        <Table.Column
          title="Mehmonlar"
          width={100}
          render={(_: unknown, record: HotelBooking) => (
            <span>
              {record.adults} kata{record.children ? ` + ${record.children} bola` : ''}
            </span>
          )}
        />
        <Table.Column
          title="Summa"
          dataIndex="totalAmount"
          width={150}
          render={(amount: number, record: HotelBooking) => (
            <strong>{formatCurrency(amount, record.currency)}</strong>
          )}
        />
        <Table.Column
          title="Holat"
          dataIndex="status"
          width={130}
          render={(status: string) => (
            <Tag color={statusColors[status] || 'default'}>{statusLabels[status] || status}</Tag>
          )}
        />
        <Table.Column
          title="Yaratilgan"
          dataIndex="createdAt"
          width={110}
          render={(date: string) => formatDate(date)}
        />
        <Table.Column
          title="Amallar"
          width={70}
          render={(_: unknown, record: HotelBooking) => (
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/hotels/bookings/${record.id}`)}
            />
          )}
        />
      </Table>
    </List>
  );
};
